import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import API_URL from '../config/api';
import Comments from '../components/Comments';
import SEO from '../components/SEO';
import analytics from '../utils/analytics';

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlogPost = async () => {
      try {
        const response = await fetch(`${API_URL}/api/content-items/${id}`);
        if (!response.ok) throw new Error('Failed to fetch blog post');
        const result = await response.json();
        console.log('Blog API response:', result);
        const blogData = result.data;
        setBlog(blogData);
        
        // Track blog post view
        if (blogData) {
          analytics.viewBlogPost(blogData._id, blogData.title);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPost();
  }, [id]);

  if (loading) {
    return (
      <Container>
        <ContentWrapper>
          <LoadingMessage>Loading article...</LoadingMessage>
        </ContentWrapper>
      </Container>
    );
  }

  if (error || !blog) {
    return (
      <>
        <SEO
          title="Blog Post Not Found"
          description="The requested blog post could not be found."
        />
        <Container>
          <ContentWrapper>
            <ErrorMessage>Failed to load article. Please try again.</ErrorMessage>
            <BackButton onClick={() => navigate('/')}>← Back to Home</BackButton>
          </ContentWrapper>
        </Container>
      </>
    );
  }

  const isMarkdown = blog.format === 'markdown';
  const blogDate = blog.date || blog.createdAt;
  const publishedTime = blogDate ? new Date(blogDate).toISOString() : undefined;
  const modifiedTime = blog.updatedAt ? new Date(blog.updatedAt).toISOString() : undefined;

  // Generate structured data for blog post
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: blog.title,
    description: blog.shortDescription || blog.description || '',
    image: blog.image || undefined,
    datePublished: publishedTime,
    dateModified: modifiedTime || publishedTime,
    author: {
      '@type': 'Person',
      name: blog.author || 'Richard Plotka',
    },
    publisher: {
      '@type': 'Person',
      name: 'Richard Plotka',
    },
  };

  return (
    <>
      <SEO
        title={blog.title}
        description={blog.shortDescription || blog.description || ''}
        image={blog.image}
        type="article"
        keywords={blog.tags || []}
        publishedTime={publishedTime}
        modifiedTime={modifiedTime}
        structuredData={structuredData}
      />
      <Container>
        <ContentWrapper
          as={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <BackButton onClick={() => navigate('/')}>← Back to Home</BackButton>
          
          <ArticleHeader>
            <Category>{blog.category}</Category>
            <Title>{blog.title}</Title>
          <Meta>
            <Author>By {blog.author || 'Richard Plotka'}</Author>
            <Separator>•</Separator>
            <DateText>{new Date(blog.date || blog.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</DateText>
            {blog.readTime && (
              <>
                <Separator>•</Separator>
                <ReadTime>{blog.readTime} min read</ReadTime>
              </>
            )}
          </Meta>
          {blog.tags && blog.tags.length > 0 && (
            <Tags>
              {blog.tags.map((tag, idx) => (
                <Tag key={idx}>{tag}</Tag>
              ))}
            </Tags>
          )}
        </ArticleHeader>

        <ArticleContent>
          {isMarkdown ? (
            <MarkdownContent>
              <ReactMarkdown>{blog.content || ''}</ReactMarkdown>
            </MarkdownContent>
          ) : (
            <HTMLContent dangerouslySetInnerHTML={{ __html: blog.content || '' }} />
          )}
        </ArticleContent>

        <BackButton onClick={() => navigate('/blog')}>← Back to Blog</BackButton>

        {/* Comments Section */}
        <Comments blogId={id} />
      </ContentWrapper>
    </Container>
    </>
  );
};

const Container = styled.div`
  min-height: 100vh;
  padding: 100px 2rem 4rem;
  background: var(--bg-primary);
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const BackButton = styled.button`
  background: transparent;
  border: 2px solid var(--accent);
  color: var(--accent);
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 2rem;

  &:hover {
    background: var(--accent);
    color: white;
    transform: translateX(-4px);
  }
`;

const ArticleHeader = styled.header`
  margin-bottom: 3rem;
  padding-bottom: 2rem;
  border-bottom: 2px solid var(--border-color, rgba(255, 255, 255, 0.1));
`;

const Category = styled.div`
  display: inline-block;
  background: var(--accent-gradient);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 1rem;
`;

const Title = styled.h1`
  font-size: clamp(2rem, 5vw, 3rem);
  color: var(--text-primary);
  margin-bottom: 1rem;
  line-height: 1.2;
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.95rem;
  margin-bottom: 1rem;
`;

const Author = styled.span`
  font-weight: 600;
`;

const Separator = styled.span`
  opacity: 0.5;
`;

const DateText = styled.span``;

const ReadTime = styled.span``;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const Tag = styled.span`
  background: var(--bg-secondary);
  color: var(--text-primary);
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  font-size: 0.85rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ArticleContent = styled.article`
  color: var(--text-primary);
  line-height: 1.8;
  font-size: 1.1rem;
  margin-bottom: 3rem;
`;

const HTMLContent = styled.div`
  p {
    margin-bottom: 1.5rem;
    color: var(--text-primary);
  }

  i, em {
    font-style: italic;
    color: var(--accent);
  }

  strong, b {
    font-weight: 700;
    color: var(--text-primary);
  }

  br {
    margin-bottom: 0.5rem;
  }
`;

const MarkdownContent = styled.div`
  h1, h2, h3, h4, h5, h6 {
    color: var(--text-primary);
    margin-top: 2rem;
    margin-bottom: 1rem;
    font-weight: 700;
  }

  h1 {
    font-size: 2.5rem;
    border-bottom: 3px solid var(--accent);
    padding-bottom: 0.5rem;
  }

  h2 {
    font-size: 2rem;
    border-bottom: 2px solid rgba(255, 255, 255, 0.1);
    padding-bottom: 0.3rem;
  }

  h3 {
    font-size: 1.5rem;
  }

  p {
    margin-bottom: 1.5rem;
    color: var(--text-primary);
  }

  strong, b {
    font-weight: 700;
    color: var(--accent);
  }

  em, i {
    font-style: italic;
  }

  code {
    background: var(--bg-secondary);
    padding: 0.2rem 0.4rem;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    font-size: 0.95em;
  }

  pre {
    background: var(--bg-secondary);
    padding: 1rem;
    border-radius: 8px;
    overflow-x: auto;
    margin-bottom: 1.5rem;

    code {
      background: none;
      padding: 0;
    }
  }

  ul, ol {
    margin-bottom: 1.5rem;
    padding-left: 2rem;
  }

  li {
    margin-bottom: 0.5rem;
  }

  blockquote {
    border-left: 4px solid var(--accent);
    padding-left: 1rem;
    margin: 1.5rem 0;
    font-style: italic;
    color: var(--text-secondary);
  }

  hr {
    border: none;
    border-top: 2px solid rgba(255, 255, 255, 0.1);
    margin: 2rem 0;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
  font-size: 1.2rem;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #ff6b6b;
  font-size: 1.2rem;
  margin-bottom: 2rem;
`;

export default BlogDetail;
